import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Clock } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Skeleton } from '../components/Skeleton';
import BlueprintCtaCard from '../components/BlueprintCtaCard';
import NewsletterSignup from '../components/NewsletterSignup';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric', timeZone: 'America/Chicago'});
  } catch {
    return '';
  }
};

const ALL_TAB = { id: 'all', label: 'All' };

// ============== CATEGORY TABS ==============
const CategoryTabs = ({ categories, active, onChange }) => (
  <div
    className="flex items-center gap-2 overflow-x-auto pb-2 -mx-1 px-1 mb-10 no-scrollbar"
    data-testid="news-category-tabs"
  >
    {[ALL_TAB, ...categories].map((c) => {
      const isActive = active === c.id;
      return (
        <button
          key={c.id}
          type="button"
          onClick={() => onChange(c.id)}
          className={`px-4 py-2 whitespace-nowrap font-mono uppercase text-[11px] tracking-[0.12em] transition-all ${
            isActive
              ? 'bg-[#0A1F2E] text-[#F9F6EF]'
              : 'text-[#0A1F2E]/65 hover:text-[#0A1F2E] border-b border-transparent hover:border-[#0A1F2E]/30'
          }`}
          data-testid={`news-tab-${c.id}`}
        >
          {c.label}
        </button>
      );
    })}
  </div>
);

// ============== CENTER HERO (oversized teal-italic headline) ==============
const CenterHero = ({ article }) => (
  <Link
    to={`/relo-news/${article.slug}`}
    className="group block"
    data-testid="news-hero"
  >
    <div className="relative aspect-[4/3] md:aspect-[5/4] overflow-hidden bg-[#F4ECD8] mb-6">
      <img
        src={article.hero_image_url}
        alt=""
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        loading="eager"
      />
    </div>
    <div className="flex items-center gap-2 mb-4">
      <span
        className="font-mono uppercase text-[10px] tracking-[0.2em] text-[#E8A830] font-semibold"
        data-testid="news-hero-category"
      >
        {article.category_label}
      </span>
      <span className="w-1 h-1 rounded-full bg-[#0A1F2E]/25" />
      <span className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/55 flex items-center gap-1">
        <Clock className="w-2.5 h-2.5" /> {article.read_time}
      </span>
    </div>
    <h2
      className="font-display italic text-[#2CA8A0] group-hover:text-[#1A5C6E] transition leading-[0.95] text-4xl sm:text-5xl md:text-6xl lg:text-[68px] mb-5"
      data-testid="news-hero-title"
    >
      {article.title}
    </h2>
    <p className="font-body text-[#0A1F2E]/75 text-base md:text-lg leading-relaxed max-w-xl">
      {article.excerpt}
    </p>
    <p className="mt-5 font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/45">
      {(typeof article.author === 'object' ? article.author?.name : article.author) || 'Mundalo Editorial'} · {formatDate(article.published_at)}
    </p>
  </Link>
);

// ============== SIDE STACK CARD ==============
const SideCard = ({ article }) => (
  <Link
    to={`/relo-news/${article.slug}`}
    className="group block"
    data-testid={`news-card-${article.slug}`}
  >
    <div className="aspect-[4/3] overflow-hidden bg-[#F4ECD8] mb-3">
      <img
        src={article.hero_image_url}
        alt=""
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        loading="lazy"
      />
    </div>
    <div className="flex items-center gap-2 mb-2">
      <span className="font-mono uppercase text-[9px] tracking-[0.18em] text-[#E8A830] font-semibold">
        {article.category_label}
      </span>
      <span className="w-1 h-1 rounded-full bg-[#0A1F2E]/25" />
      <span className="font-mono uppercase text-[9px] tracking-[0.12em] text-[#0A1F2E]/55">
        {article.read_time}
      </span>
    </div>
    <h3 className="font-display italic text-[#0A1F2E] group-hover:text-[#1A5C6E] transition text-xl md:text-2xl leading-[1.1] mb-2">
      {article.title}
    </h3>
    <p className="font-body text-[#0A1F2E]/65 text-sm leading-snug line-clamp-2">
      {article.excerpt}
    </p>
  </Link>
);

// ============== TRENDING ROW (numbered, black badges) ==============
const TrendingRow = ({ articles }) => {
  if (!articles?.length) return null;
  return (
    <section
      className="border-t border-[#0A1F2E]/15 pt-12 mb-16"
      data-testid="news-trending"
    >
      <div className="flex items-baseline justify-between mb-8">
        <div>
          <p className="font-mono uppercase text-[10px] tracking-[0.22em] text-[#1A5C6E] mb-2">
            Most Read
          </p>
          <h2 className="font-display italic text-[#0A1F2E] text-3xl md:text-4xl leading-tight">
            Trending right now.
          </h2>
        </div>
      </div>

      <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-x-6 gap-y-10">
        {articles.slice(0, 5).map((a, i) => (
          <li key={a.slug} className="flex gap-4" data-testid={`news-trending-${i + 1}`}>
            <span
              className="flex-shrink-0 w-10 h-10 bg-[#0A1F2E] text-[#F9F6EF] rounded-full flex items-center justify-center font-display italic text-xl leading-none"
              aria-hidden="true"
            >
              {i + 1}
            </span>
            <Link
              to={`/relo-news/${a.slug}`}
              className="group flex-1 -mt-1"
            >
              <span className="font-mono uppercase text-[9px] tracking-[0.18em] text-[#E8A830] font-semibold">
                {a.category_label}
              </span>
              <h3 className="mt-1.5 font-display italic text-[#0A1F2E] group-hover:text-[#2CA8A0] transition text-lg md:text-xl leading-[1.15]">
                {a.title}
              </h3>
              <p className="mt-2 font-mono uppercase text-[9px] tracking-[0.12em] text-[#0A1F2E]/45">
                {a.read_time}
              </p>
            </Link>
          </li>
        ))}
      </ol>
    </section>
  );
};

// ============== SKELETON ==============
const SkeletonGrid = () => (
  <div className="grid grid-cols-12 gap-x-8 gap-y-12 mb-20">
    <div className="col-span-12 md:col-span-3 space-y-8">
      {[0, 1].map((i) => (
        <div key={i}>
          <Skeleton className="aspect-[4/3] mb-3" />
          <Skeleton className="h-3 w-20 mb-2" />
          <Skeleton className="h-6 w-full mb-2" />
          <Skeleton className="h-3 w-2/3" />
        </div>
      ))}
    </div>
    <div className="col-span-12 md:col-span-6">
      <Skeleton className="aspect-[5/4] mb-6" />
      <Skeleton className="h-4 w-32 mb-4" />
      <Skeleton className="h-12 w-full mb-2" />
      <Skeleton className="h-12 w-3/4 mb-5" />
      <Skeleton className="h-4 w-full mb-2" />
      <Skeleton className="h-4 w-2/3" />
    </div>
    <div className="col-span-12 md:col-span-3 space-y-8">
      {[0, 1].map((i) => (
        <div key={i}>
          <Skeleton className="aspect-[4/3] mb-3" />
          <Skeleton className="h-3 w-20 mb-2" />
          <Skeleton className="h-6 w-full mb-2" />
          <Skeleton className="h-3 w-2/3" />
        </div>
      ))}
    </div>
  </div>
);

// ============== REMAINDER GRID (after the masonry + CTA) ==============
const RemainderGrid = ({ articles }) => {
  if (!articles?.length) return null;
  return (
    <section className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-14 mb-20">
      {articles.map((a, idx) => (
        <React.Fragment key={a.slug}>
          <SideCard article={a} />
          {idx === 1 && <BlueprintCtaCard />}
        </React.Fragment>
      ))}
    </section>
  );
};

// ============== MAIN ==============
const ReloNews = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCategory = searchParams.get('category') || 'all';
  const initialLang = searchParams.get('lang') === 'es' ? 'es' : 'en';
  const [active, setActive] = useState(initialCategory);
  const [lang, setLang] = useState(initialLang);
  const [data, setData] = useState(null);
  const [trending, setTrending] = useState([]);
  const [cityGuides, setCityGuides] = useState(null);

  useEffect(() => {
    setData(null);
    const params = new URLSearchParams();
    if (active !== 'all') params.set('category', active);
    if (lang !== 'en') params.set('lang', lang);
    const qs = params.toString();
    const url = `${API_URL}/api/news${qs ? '?' + qs : ''}`;
    fetch(url)
      .then((r) => (r.ok ? r.json() : { articles: [], categories: [] }))
      .then(setData)
      .catch(() => setData({ articles: [], categories: [] }));
  }, [active, lang]);

  // City Guides tab - fetch the 13 guides on demand and cache once per language.
  useEffect(() => {
    if (active !== 'city-guides') return;
    if (cityGuides && cityGuides.lang === lang) return;
    setCityGuides(null);
    fetch(`${API_URL}/api/cities`)
      .then((r) => (r.ok ? r.json() : { cities: [] }))
      .then((d) => setCityGuides({ lang, cities: d.cities || [] }))
      .catch(() => setCityGuides({ lang, cities: [] }));
  }, [active, lang]);

  useEffect(() => {
    fetch(`${API_URL}/api/news/trending?limit=5${lang === 'es' ? '&lang=es' : ''}`)
      .then((r) => (r.ok ? r.json() : { articles: [] }))
      .then((d) => setTrending(d.articles || []))
      .catch(() => setTrending([]));
  }, [lang]);

  const onTabChange = (id) => {
    setActive(id);
    const next = new URLSearchParams(searchParams);
    if (id === 'all') next.delete('category');
    else next.set('category', id);
    setSearchParams(next, { replace: true });
  };

  const onLangChange = (l) => {
    setLang(l);
    const next = new URLSearchParams(searchParams);
    if (l === 'en') next.delete('lang');
    else next.set('lang', l);
    setSearchParams(next, { replace: true });
  };

  const { articles, categories } = data || { articles: [], categories: [] };

  // DMagazine masonry split:
  //  - Center hero: featured article (or first if none featured)
  //  - Left column: next 2 stacked cards
  //  - Right column: next 2 stacked cards
  //  - Remainder grid: everything after position 5
  const { centerArticle, leftStack, rightStack, restArticles } = useMemo(() => {
    if (!articles?.length) {
      return { centerArticle: null, leftStack: [], rightStack: [], restArticles: [] };
    }
    let pool = [...articles];
    let center;
    if (active === 'all') {
      const fIdx = pool.findIndex((a) => a.featured);
      center = fIdx >= 0 ? pool.splice(fIdx, 1)[0] : pool.shift();
    } else {
      center = pool.shift();
    }
    const left = pool.slice(0, 2);
    const right = pool.slice(2, 4);
    const rest = pool.slice(4);
    return { centerArticle: center, leftStack: left, rightStack: right, restArticles: rest };
  }, [articles, active]);

  return (
    <div className="min-h-screen bg-[#F9F6EF] text-[#0A1F2E]">
      <SEO
        title="Relo News - Expert Guides for Americans Moving to Mexico"
        description="Visas, housing, healthcare, banking, city guides and more. Real information from people who made the move. Updated weekly."
        path="/relo-news"
      />
      <Header />

      {/* Page header */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-16 pb-2">
        <p className="font-mono uppercase text-[11px] tracking-[0.22em] text-[#1A5C6E] mb-3">
          The Magazine · Issue {new Date().getFullYear()}
        </p>
        <h1 className="font-display italic text-5xl md:text-7xl text-[#0A1F2E] leading-[0.95]">
          Relo News
        </h1>
        <p className="font-body text-[#0A1F2E]/70 mt-4 max-w-2xl text-lg">
          Editorial coverage of the move to Mexico - straight-talking reporting on visas, housing, banking,
          healthcare, city life, community, and the numbers behind the trend.
        </p>
      </div>

      <main className="max-w-7xl mx-auto px-6 md:px-10 pt-12 pb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <Link
            to="/relo-news/cities"
            data-testid="news-city-guides-link"
            className="inline-flex items-center gap-2 font-mono uppercase text-[11px] tracking-[0.15em] font-semibold bg-[#E8A830] text-[#0A1F2E] px-4 py-2 rounded-full hover:brightness-95 transition self-start"
          >
            City Guides → 13 cities
          </Link>

          <div
            className="inline-flex items-center bg-white rounded-full p-1 border border-[#EFE6CF] shadow-sm self-start"
            role="tablist"
            aria-label="Language"
            data-testid="news-lang-toggle"
          >
            <button
              type="button"
              role="tab"
              aria-selected={lang === 'en'}
              onClick={() => onLangChange('en')}
              data-testid="news-lang-en"
              className={`px-4 py-1.5 rounded-full font-mono uppercase text-[10px] tracking-[0.12em] transition ${
                lang === 'en' ? 'bg-[#F9F6EF] text-[#0A1F2E] border-2 border-[#2CA8A0] font-semibold' : 'text-[#0A1F2E]/60'
              }`}
            >EN</button>
            <button
              type="button"
              role="tab"
              aria-selected={lang === 'es'}
              onClick={() => onLangChange('es')}
              data-testid="news-lang-es"
              className={`px-4 py-1.5 rounded-full font-mono uppercase text-[10px] tracking-[0.12em] transition ${
                lang === 'es' ? 'bg-[#F9F6EF] text-[#0A1F2E] border-2 border-[#2CA8A0] font-semibold' : 'text-[#0A1F2E]/60'
              }`}
            >ES</button>
          </div>
        </div>

        <CategoryTabs
          categories={categories || []}
          active={active}
          onChange={onTabChange}
        />

        {active === 'city-guides' ? (
          !cityGuides ? (
            <SkeletonGrid />
          ) : (
            <section
              data-testid="news-city-guides-grid"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14 mb-20"
              style={{ animation: 'fadeIn 0.35s ease-out' }}
            >
              {cityGuides.cities.map((city) => (
                <Link
                  key={city.slug}
                  to={`/relo-news/cities/${city.slug}`}
                  className="group block"
                  data-testid={`news-city-card-${city.slug}`}
                >
                  <div className="aspect-[4/3] overflow-hidden bg-[#F4ECD8] mb-3">
                    <img
                      src={city.hero_image_url}
                      alt={`${city.name} hero`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-mono uppercase text-[9px] tracking-[0.18em] text-[#E8A830] font-semibold">
                      City Guide
                    </span>
                    <span className="w-1 h-1 rounded-full bg-[#0A1F2E]/25" />
                    <span className="font-mono uppercase text-[9px] tracking-[0.12em] text-[#0A1F2E]/55">
                      {city.state}
                    </span>
                  </div>
                  <h3 className="font-display italic text-[#0A1F2E] group-hover:text-[#1A5C6E] transition text-xl md:text-2xl leading-[1.1] mb-2">
                    {city.name}
                  </h3>
                  {city.tagline && (
                    <p className="font-body text-[#0A1F2E]/65 text-sm leading-snug line-clamp-2">
                      {city.tagline}
                    </p>
                  )}
                </Link>
              ))}
            </section>
          )
        ) : !data ? (
          <SkeletonGrid />
        ) : !articles?.length ? (
          <div className="text-center py-24" data-testid="news-empty">
            <p className="font-display italic text-3xl text-[#0A1F2E]/65 mb-4">
              No articles in this category yet.
            </p>
            <p className="font-body text-[#0A1F2E]/55 max-w-md mx-auto">
              Our editors refresh the feed every two weeks. Check back soon, or browse another category.
            </p>
            <button
              type="button"
              onClick={() => onTabChange('all')}
              className="mt-6 font-mono uppercase text-[11px] tracking-[0.12em] font-semibold bg-[#0A1F2E] text-[#F9F6EF] px-5 py-2.5 rounded-full hover:brightness-110 transition"
              data-testid="news-empty-all-btn"
            >
              Show all stories
            </button>
          </div>
        ) : (
          <div
            key={active}
            style={{ animation: 'fadeIn 0.35s ease-out' }}
            data-testid="news-feed"
          >
            {/* ===== DMagazine masonry ===== */}
            <section className="grid grid-cols-12 gap-x-8 gap-y-12 mb-20" data-testid="news-masonry">
              {/* Left column - stacked secondary */}
              <div className="col-span-12 md:col-span-3 order-2 md:order-1 space-y-10">
                {leftStack.map((a) => <SideCard key={a.slug} article={a} />)}
              </div>

              {/* Center hero */}
              <div className="col-span-12 md:col-span-6 order-1 md:order-2">
                {centerArticle && <CenterHero article={centerArticle} />}
              </div>

              {/* Right column - stacked secondary */}
              <div className="col-span-12 md:col-span-3 order-3 space-y-10">
                {rightStack.map((a) => <SideCard key={a.slug} article={a} />)}
              </div>
            </section>

            {/* ===== Trending numbered row ===== */}
            <TrendingRow articles={trending.length ? trending : articles} />

            {/* ===== Remainder grid with inline Blueprint CTA ===== */}
            <RemainderGrid articles={restArticles} />
          </div>
        )}
      </main>

      {/* Newsletter signup band */}
      <NewsletterSignup source="relo-news-grid" />

      <Footer />

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { scrollbar-width: none; }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default ReloNews;
